/**
 * Tap-to-define.
 *
 * Uses click (not mousedown/touchstart) so drag-to-select still works —
 * browsers suppress click after a selection gesture.
 * Words are segmented lazily with Intl.Segmenter on first interaction.
 */
import { fetchDefinition } from "./api.js";
import { LANG_BCP47, LANGUAGES } from "./config.js";

const segmenterCache = new Map();

function getSegmenter(lang) {
  const locale = LANG_BCP47[lang] || lang || "en";
  if (segmenterCache.has(locale)) return segmenterCache.get(locale);
  let seg = null;
  if (typeof Intl !== "undefined" && Intl.Segmenter) {
    try {
      seg = new Intl.Segmenter(locale, { granularity: "word" });
    } catch {
      seg = new Intl.Segmenter("en", { granularity: "word" });
    }
  }
  segmenterCache.set(locale, seg);
  return seg;
}

function wrapTextNode(textNode, lang) {
  const text = textNode.nodeValue;
  if (!text || !text.trim()) return;

  const seg = getSegmenter(lang);
  const frag = document.createDocumentFragment();

  if (!seg) {
    const parts = text.split(/(\s+)/);
    for (const part of parts) {
      if (!part) continue;
      if (/^\s+$/.test(part)) {
        frag.appendChild(document.createTextNode(part));
      } else {
        const span = document.createElement("span");
        span.className = "word";
        span.dataset.word = part;
        span.textContent = part;
        frag.appendChild(span);
      }
    }
  } else {
    for (const { segment, isWordLike } of seg.segment(text)) {
      if (!isWordLike) {
        frag.appendChild(document.createTextNode(segment));
        continue;
      }
      const span = document.createElement("span");
      span.className = "word";
      span.dataset.word = segment;
      span.textContent = segment;
      frag.appendChild(span);
    }
  }

  textNode.parentNode.replaceChild(frag, textNode);
}

function segmentElement(el, lang) {
  if (!el || el.dataset.segmented === "1") return;
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (parent.closest("a.article__source-link")) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  for (const n of nodes) wrapTextNode(n, lang);
  el.dataset.segmented = "1";
}

function segmentArticle(article) {
  if (!article || article.dataset.segmented === "1") return;
  const lang = article.dataset.lang || "en";
  const title = article.querySelector(".article__title");
  const summary = article.querySelector(".article__summary");
  if (title) segmentElement(title, lang);
  if (summary) segmentElement(summary, lang);
  article.dataset.segmented = "1";
}

function isMobile() {
  return window.matchMedia("(max-width: 719px)").matches;
}

export function createDefineUI(root) {
  const panel = root.querySelector(".define__panel");
  const wordEl = root.querySelector("#define-word");
  const langEl = root.querySelector("#define-lang");
  const bodyEl = root.querySelector("#define-body");
  const linkEl = root.querySelector("#define-link");
  let activeWordEl = null;
  let open = false;

  function dismiss() {
    if (!open) return;
    open = false;
    root.hidden = true;
    if (activeWordEl) {
      activeWordEl.classList.remove("is-active");
      activeWordEl = null;
    }
  }

  function positionPanel(anchor) {
    if (isMobile()) {
      panel.style.top = "";
      panel.style.left = "";
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const pad = 8;
    const width = Math.min(22 * 16, window.innerWidth - 32);
    let left = rect.left + rect.width / 2 - width / 2;
    left = Math.max(pad, Math.min(left, window.innerWidth - width - pad));
    let top = rect.bottom + pad;
    panel.style.width = `${width}px`;
    root.hidden = false;
    const h = panel.getBoundingClientRect().height || 200;
    if (top + h > window.innerHeight - pad) {
      top = Math.max(pad, rect.top - h - pad);
    }
    panel.style.top = `${top}px`;
    panel.style.left = `${left}px`;
  }

  function showLoading(word, lang, anchor) {
    open = true;
    wordEl.textContent = word;
    const meta = LANGUAGES.find((l) => l.code === lang);
    langEl.textContent = meta ? meta.native : lang;
    bodyEl.innerHTML = `<div class="define__shimmer" aria-hidden="true"><span></span><span></span><span></span></div>`;
    linkEl.href = `https://en.wiktionary.org/wiki/${encodeURIComponent(word)}`;
    if (activeWordEl) activeWordEl.classList.remove("is-active");
    activeWordEl = anchor;
    anchor.classList.add("is-active");
    positionPanel(anchor);
    root.hidden = false;
  }

  function showResult(data) {
    if (data.url) linkEl.href = data.url;
    if (data.miss || !data.definitions?.length) {
      bodyEl.innerHTML = `<p class="define__miss">No definition found for “${escapeText(data.word)}”.</p>`;
      return;
    }
    const ul = document.createElement("ul");
    ul.className = "define__list";
    for (const d of data.definitions) {
      const li = document.createElement("li");
      if (d.partOfSpeech) {
        const pos = document.createElement("span");
        pos.className = "define__pos";
        pos.textContent = d.partOfSpeech;
        li.appendChild(pos);
      }
      const p = document.createElement("p");
      p.className = "define__gloss";
      p.textContent = d.gloss;
      li.appendChild(p);
      ul.appendChild(li);
    }
    bodyEl.replaceChildren(ul);
    if (!isMobile()) positionPanel(activeWordEl || panel);
  }

  root.addEventListener("click", (e) => {
    if (e.target.closest("[data-dismiss]")) dismiss();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") dismiss();
  });

  async function openForWord(wordElNode, lang) {
    const word = wordElNode.dataset.word || wordElNode.textContent;
    if (!word?.trim()) return;
    showLoading(word, lang, wordElNode);
    try {
      const data = await fetchDefinition(word, lang);
      if (open) showResult(data);
    } catch {
      if (open) {
        bodyEl.innerHTML = `<p class="define__miss">Couldn't look that up right now.</p>`;
      }
    }
  }

  return {
    dismiss,
    bind(articlesEl) {
      articlesEl.addEventListener("click", (e) => {
        const article = e.target.closest(".article");
        if (!article) return;

        // Don't fight expand / source-link controls
        if (e.target.closest(".article__expand, .article__source-link, .article__actions")) return;

        segmentArticle(article);

        let word = e.target.closest?.(".word");
        if (!word || !article.contains(word)) {
          const under = document.elementFromPoint(e.clientX, e.clientY);
          word = under?.closest?.(".word");
        }
        if (!word || !article.contains(word)) return;

        e.preventDefault();
        e.stopPropagation();
        openForWord(word, article.dataset.lang || "en");
      });
    },
  };
}

function escapeText(s) {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
